import { useState, useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../components/Toast';

export function Verification() {
  const location = useLocation();
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const email = (location.state as { email?: string })?.email || '';
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResend = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (countdown > 0) return;
    
    if (!email) {
      toast.error('No email address found. Please sign up again.');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });

      if (error) throw error;

      toast.success('Verification email sent! Check your inbox.');
      setCountdown(60); // Start 60 second cooldown (matches Supabase rate limit)
    } catch (error) {
      console.error('Error resending verification:', error);
      toast.error('Failed to resend email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Already verified (or verified in another tab, which syncs the session here).
  // AppLayout sends anyone who hasn't finished onboarding on to it.
  if (user?.email_confirmed_at) {
    return <Navigate to="/overview" replace />;
  }

  // Reached directly (no email from signup/login), so there's nothing to resend
  if (!email) {
    return <Navigate to="/login" replace />;
  }

  const isDisabled = loading || countdown > 0;

  const getButtonText = () => {
    if (loading) return 'Sending...';
    if (countdown > 0) return `Resend verification email ${countdown}s`;
    return 'Resend verification email';
  };

  return (
    <div className="page-wrapper">
      <div className="app-wrapper">
        <div className="section-verify padding-global">
          <div className="container-small">
            <div className="max-width-medium align-center">
              <div className="card is-verification">
                <div className="intro-wrapper">
                  <img
                    width={120}
                    loading="lazy"
                    alt="Lima shimmering logo"
                    src="/images/lima-logomark-1_1lima-logomark-1.avif"
                    className="medium-shimmering-logo"
                  />
                  <div className="spacer-medium"></div>
                  <div className="max-width-xsmall align-center">
                    <div className="text-size-medium text-weight-semibold text-colour-dark-brown">
                      Please verify your email to continue
                    </div>
                  </div>
                  <div className="spacer-xtiny"></div>
                  <div className="text-size-regular text-colour-alt-grey align-center max-width-small">
                    Click the link in the email to verify your account
                  </div>
                  <div className="spacer-small"></div>
                  
                  <div className="align-content-middle">
                    <a
                      href="#"
                      className={`button w-button ${isDisabled ? 'is-current' : ''}`}
                      onClick={handleResend}
                      style={{ pointerEvents: isDisabled ? 'none' : 'auto' }}
                    >
                      {getButtonText()}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
